"use client";

import { usePortfolioBuilderStore } from "@/stores/portfolioBuilderStore";

const FIELDS = [
  {
    key: "annualIncome" as const,
    label: "Annual Income",
    min: 0,
    max: 10_000_000,
    step: 10_000,
    prefix: "$",
  },
  {
    key: "liquidNetWorth" as const,
    label: "Liquid Net Worth",
    min: 0,
    max: 50_000_000,
    step: 50_000,
    prefix: "$",
    desc: "Cash, stocks, bonds — excludes real estate",
  },
  {
    key: "sovrunAllocation" as const,
    label: "Sovrun Allocation",
    min: 10_000,
    max: 10_000_000,
    step: 10_000,
    prefix: "$",
    desc: "How much you want to allocate to Sovrun strategies",
  },
];

function formatCurrency(n: number): string {
  return n.toLocaleString("en-US");
}

export function FinancialStep() {
  const store = usePortfolioBuilderStore();

  const allocationPct =
    store.liquidNetWorth > 0
      ? ((store.sovrunAllocation / store.liquidNetWorth) * 100).toFixed(1)
      : "0.0";

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Financial Profile</h2>
        <p className="mt-1 text-sm text-gray-400">
          Help us understand your financial situation to calibrate the recommendation.
        </p>
      </div>

      <div className="space-y-5">
        {FIELDS.map(({ key, label, min, max, step, prefix, desc }) => (
          <div key={key} className="rounded-lg border border-gray-800 bg-gray-900/50 p-4">
            <div className="mb-2 flex items-center justify-between">
              <div>
                <span className="font-medium">{label}</span>
                {desc && <p className="text-xs text-gray-500">{desc}</p>}
              </div>
              <span className="text-sm font-medium text-blue-400">
                {prefix}
                {formatCurrency(store[key])}
              </span>
            </div>
            <input
              type="range"
              min={min}
              max={max}
              step={step}
              value={store[key]}
              onChange={(e) => store.setFinancial(key, Number(e.target.value))}
              className="w-full accent-blue-500"
            />
            <div className="mt-1 flex justify-between text-xs text-gray-600">
              <span>
                {prefix}
                {formatCurrency(min)}
              </span>
              <span>
                {prefix}
                {formatCurrency(max)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-gray-800 bg-gray-900/50 p-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Allocation as % of net worth</span>
          <span
            className={`font-medium ${
              Number(allocationPct) > 30 ? "text-amber-400" : "text-green-400"
            }`}
          >
            {allocationPct}%
          </span>
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={() => store.prevStep()}
          className="rounded-lg border border-gray-700 px-6 py-2.5 text-sm text-gray-300 hover:bg-gray-800 transition-colors"
        >
          Back
        </button>
        <button
          onClick={() => store.nextStep()}
          className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium hover:bg-blue-500 transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
}
